import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe, LocalizationService, NgxValidateCoreModule } from '@abp/ng.core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TextareaModule } from 'primeng/textarea';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CxInputPendingLoaderDirective } from 'src/app/shared/directives/cx-input-pending-loader.directive';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { FilterOperator } from 'src/core/models/request/filter-operator.enum';
import { CrudService } from 'src/core/services/crud/crud.service';
import { WrapUpDefinitionDto } from 'src/core/models/wrap-up-definition/wrap-up-definition.dto';
import { WrapUpOutcomePolicy } from 'src/core/models/wrap-up-definition/wrap-up-outcome-policy.enum';
import { WrapUpPolicyOption } from 'src/core/models/wrap-up-definition/wrap-up-policy-option.model';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { QueueDto } from 'src/app/administration/settings/queues/models/queue.dto';
import { WrapUpDefinitionService } from '../../services/wrap-up-definition.service';
import { Subject } from 'rxjs';

const DESCRIPTION_MAX_LENGTH = 500;

@Component({
  selector: 'app-wrap-up-definition-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LocalizationPipe,
    NgxValidateCoreModule,
    DialogModule,
    InputTextModule,
    MultiSelectModule,
    TextareaModule,
    CxSelectComponent,
    CxInputPendingLoaderDirective,
    FormActionsComponent,
  ],
  templateUrl: './wrap-up-definition-form-dialog.component.html',
})
export class WrapUpDefinitionFormDialogComponent extends BaseFormComponent<WrapUpDefinitionDto> {
  private readonly fb = inject(FormBuilder);
  private readonly crudService = inject(CrudService);
  private readonly wrapUpService = inject(WrapUpDefinitionService);
  private readonly localization = inject(LocalizationService);
  private readonly destroy$ = new Subject<void>();
  private currentCodeName = '';

  availableQueues: QueueDto[] = [];
  queuesLoading = false;

  policyOptions: WrapUpPolicyOption[] = this.buildPolicyOptions();

  readonly descriptionMaxLength = DESCRIPTION_MAX_LENGTH;

  @Input() visible = false;
  @Input() codeId: number | null = null;
  @Input() isPredefined = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  get isEditMode(): boolean {
    return this.codeId != null;
  }

  get isValidating(): boolean {
    return this.form?.pending ?? false;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isValidating;
  }

  get headerKey(): string {
    if (this.isEditMode && this.isPredefined) {
      return 'Settings::WorkEnvironment:WrapupCodes:AssignQueuesTitle';
    }
    return this.isEditMode
      ? 'Settings::WorkEnvironment:WrapupCodes:EditCodeTitle'
      : 'Settings::WorkEnvironment:WrapupCodes:NewCodeTitle';
  }

  get descriptionLength(): number {
    return String(this.form?.get('description')?.value ?? '').length;
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      name: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [this.uniqueNameValidator()],
        updateOn: 'change',
      }),
      description: this.fb.control('', {
        validators: [Validators.maxLength(DESCRIPTION_MAX_LENGTH)],
      }),
      policy: this.fb.control(WrapUpOutcomePolicy.Completed, {
        validators: [Validators.required],
      }),
      queueIds: this.fb.control([] as number[]),
    });
  }

  protected override afterFormInit(): void {
    this.localization.languageChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.policyOptions = this.buildPolicyOptions();
      this.cdr.markForCheck();
    });

    if (this.visible) {
      this.loadQueues();
    }
    this.syncFormFromInputs();
  }

  private buildPolicyOptions(): WrapUpPolicyOption[] {
    return [
      {
        label: this.localization.instant('Settings::WorkEnvironment:WrapupCodes:PolicyCompleted'),
        value: WrapUpOutcomePolicy.Completed,
      },
      {
        label: this.localization.instant('Settings::WorkEnvironment:WrapupCodes:PolicyReschedule'),
        value: WrapUpOutcomePolicy.Reschedule,
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && !changes['visible'].previousValue) {
      this.loadQueues();
    }
    this.syncFormFromInputs();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (this.isValidating) {
      return;
    }

    this.onSubmit();
  }

  protected submitRequest(): Observable<unknown> {
    const { name, description, policy, queueIds: rawQueueIds } = this.form.getRawValue();
    const trimmedName = String(name ?? '').trim();
    const trimmedDescription = String(description ?? '').trim();
    const queueIds = (rawQueueIds ?? []) as number[];

    const payload = {
      name: trimmedName,
      description: trimmedDescription || null,
      isActive: true,
      policy: (policy ?? WrapUpOutcomePolicy.Completed) as WrapUpOutcomePolicy,
      queueIds,
    };

    if (this.isEditMode) {
      return this.crudService.update<WrapUpDefinitionDto>(WrapUpDefinitionDto, {
        id: this.codeId!,
        ...payload,
      });
    }

    return this.crudService.insertWithoutId(WrapUpDefinitionDto, payload);
  }

  protected override onSubmitSuccess(): void {
    super.onSubmitSuccess();
    this.saved.emit();
    this.visibleChange.emit(false);
  }

  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return of(null);
      }

      if (
        this.codeId != null &&
        value.toLocaleLowerCase() === this.currentCodeName.toLocaleLowerCase()
      ) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() =>
          this.wrapUpService.checkNameUnique(value, this.codeId ?? undefined),
        ),
        map(isUnique => (isUnique ? null : { nameNotUnique: true })),
        catchError(() => of(null)),
        take(1),
      );
    };
  }

  private syncFormFromInputs(): void {
    if (!this.visible || !this.form) {
      return;
    }

    if (this.codeId == null) {
      this.currentCodeName = '';
      this.form.get('name')?.enable();
      this.form.get('policy')?.enable();
      this.form.get('description')?.enable();
      this.resetForm({
        name: '',
        description: '',
        queueIds: [],
        policy: WrapUpOutcomePolicy.Completed,
      });
      return;
    }

    if (this.isPredefined) {
      this.form.get('name')?.disable();
      this.form.get('policy')?.disable();
      this.form.get('description')?.disable();
    } else {
      this.form.get('name')?.enable();
      this.form.get('policy')?.enable();
      this.form.get('description')?.enable();
    }

    this.crudService
      .getById<WrapUpDefinitionDto>(WrapUpDefinitionDto, this.codeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: code => {
          const name = code?.name ?? '';
          const description = code?.description ?? '';
          const queueIds = code?.queueIds ?? [];
          const policy = code?.policy ?? WrapUpOutcomePolicy.Completed;
          this.currentCodeName = name.trim();
          this.resetForm({ name, description, queueIds, policy });

          if (this.isPredefined) {
            this.form.get('name')?.disable();
            this.form.get('policy')?.disable();
            this.form.get('description')?.disable();
          }
        },
        error: () => {
          this.currentCodeName = '';
          this.form.get('name')?.enable();
          this.form.get('policy')?.enable();
          this.form.get('description')?.enable();
          this.resetForm({
            name: '',
            description: '',
            queueIds: [],
            policy: WrapUpOutcomePolicy.Completed,
          });
        },
      });
  }

  private loadQueues(): void {
    this.queuesLoading = true;

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [{ field: 'isActive', operator: FilterOperator.Equals, value: true }],
      sorters: [],
    };

    this.crudService
      .get<QueueDto>(QueueDto, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.availableQueues = (response.items ?? []).filter(q => q.isActive);
          this.queuesLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.queuesLoading = false;
          this.cdr.markForCheck();
        },
      });
  }
}
